import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ranksService, { Rank } from '../../services/ranksService';
import ImageModal from '../common/ImageModal';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(0, 25, 0, 0.95);
  border: 2px solid #00d4ff;
  border-radius: 12px;
  padding: 30px;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
`;

const ModalTitle = styled.h2<{ $faction: 'human' | 'alien' }>`
  color: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  font-size: 24px;
  text-transform: uppercase;
  margin: 0;
  font-family: 'Courier New', monospace;
`;

const CloseButton = styled.button`
  background: none;
  border: 2px solid #00d4ff;
  color: #00d4ff;
  font-size: 20px;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #00d4ff;
    color: #000;
  }
`;

const RanksList = styled.div`
  display: grid;
  gap: 15px;
`;

const RankItem = styled.div<{ $faction: 'human' | 'alien'; $achieved: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 15px 20px;
  background: ${props => 
    props.$achieved 
      ? (props.$faction === 'alien' 
          ? 'rgba(255, 136, 0, 0.15)' 
          : 'rgba(59, 130, 246, 0.15)')
      : 'rgba(255, 255, 255, 0.05)'
  };
  border: 1px solid ${props => 
    props.$achieved 
      ? (props.$faction === 'alien' ? '#ff8800' : '#3b82f6')
      : 'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 8px;
  opacity: ${props => props.$achieved ? 1 : 0.6};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => 
      props.$faction === 'alien' 
        ? 'rgba(255, 136, 0, 0.2)' 
        : 'rgba(59, 130, 246, 0.2)'
    };
    border-color: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  }
`;

const RankIcon = styled.div<{ $faction: 'human' | 'alien' }>`
  width: 60px;
  height: 60px;
  background: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$faction === 'alien' ? '#fff' : '#000'};
  font-weight: bold;
  font-size: 12px;
  flex-shrink: 0;
`;

const RankInfo = styled.div`
  flex: 1;
`;

const RankLevel = styled.div`
  color: #888;
  font-size: 12px;
  margin-bottom: 5px;
`;

const RankName = styled.div<{ $faction: 'human' | 'alien' }>`
  color: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
`;

const RankPoints = styled.div`
  color: #ccc;
  font-size: 14px;
`;

const ProgressInfo = styled.div`
  text-align: right;
  color: #888;
  font-size: 12px;
`;

const CurrentRankIndicator = styled.div<{ $faction: 'human' | 'alien' }>`
  background: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  color: ${props => props.$faction === 'alien' ? '#fff' : '#000'};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
`;


interface RanksModalProps {
  isOpen: boolean;
  onClose: () => void;
  faction: 'human' | 'alien';
  currentPoints: number;
  onRankClick: (rank: any) => void;
}

const RanksModal: React.FC<RanksModalProps> = ({
  isOpen,
  onClose,
  faction,
  currentPoints,
  onRankClick
}) => {
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [loading, setLoading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedRankName, setSelectedRankName] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadRanks();
    }
  }, [isOpen, faction]);

  const loadRanks = async () => {
    try {
      setLoading(true);
      const ranksData = await ranksService.getRanksByFaction(faction);
      setRanks(ranksData);
    } catch (error) {
      setRanks([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  
  // Определяем текущий ранг игрока (наивысший достигнутый)
  let currentRankLevel = 0;
  for (let i = ranks.length - 1; i >= 0; i--) {
    if (currentPoints >= ranks[i].pointsRequired) {
      currentRankLevel = ranks[i].level;
      break;
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRankIconClick = (rank: Rank, e: React.MouseEvent) => {
    e.stopPropagation();
    if (rank.iconUrl) {
      setSelectedImageUrl(rank.iconUrl);
      setSelectedRankName(rank.name);
      setIsImageModalOpen(true);
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImageUrl('');
    setSelectedRankName('');
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle $faction={faction}>
            {faction === 'human' ? 'Human Forces Ranks' : 'Alien Alliance Ranks'}
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#3b82f6', padding: '20px' }}>
            Загрузка рангов...
          </div>
        ) : ranks.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
            Не удалось загрузить ранги
          </div>
        ) : (
          <RanksList>
            {ranks.map((rank, index) => {
            const isAchieved = currentPoints >= rank.pointsRequired;
            const isCurrent = rank.level === currentRankLevel;
            
            return (
              <RankItem
                key={index}
                $faction={faction}
                $achieved={isAchieved}
                onClick={() => onRankClick(rank)}
              >
                <RankIcon 
                  $faction={faction}
                  onClick={(e) => handleRankIconClick(rank, e)}
                  style={{ cursor: rank.iconUrl ? 'pointer' : 'default' }}
                >
                  {rank.iconUrl ? (
                    <img src={rank.iconUrl} alt={rank.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                  ) : (
                    `${rank.level}`
                  )}
                </RankIcon>

                <RankInfo>
                  <RankLevel>Level {rank.level}</RankLevel>
                  <RankName $faction={faction}>{rank.name}</RankName>
                  <RankPoints>{rank.pointsRequired} points required</RankPoints>
                </RankInfo>

                <ProgressInfo>
                  {isCurrent && (
                    <CurrentRankIndicator $faction={faction}>
                      Current Rank
                    </CurrentRankIndicator>
                  )}
                  {isAchieved && !isCurrent && (
                    <div style={{ color: '#4CAF50', fontSize: '10px' }}>ACHIEVED</div>
                  )}
                  {!isAchieved && rank.level > currentRankLevel && (
                    <div>{rank.pointsRequired - currentPoints} points needed</div>
                  )}
                </ProgressInfo>
              </RankItem>
            );
          })}
        </RanksList>
        )}
      </ModalContent>
      
      <ImageModal
        isOpen={isImageModalOpen}
        imageUrl={selectedImageUrl}
        imageAlt={`${selectedRankName} rank icon`}
        title={`${selectedRankName} Rank`}
        onClose={closeImageModal}
      />
    </ModalOverlay>
  );
};

export default RanksModal;